import { MessageRoutingQuickDisplay } from 'app/components/MessageRoutingVisualBuilder';
import { HookFormTextField } from 'app/components/reactHookFormComponents/HookFormTextField';
// import { MessageRoutingVisualBuilder } from 'app/components/MessageRoutingVisualBuilder';
import { useGlobalTour } from 'app/components/Tourguide/Helper';
import { Button, Setting, Switch } from 'app/design-lib';
import { SettingsPanelLoader } from 'app/design-lib/components/SettingsPanelLoader.tsx';
import { useUpdateCallflowPartial } from 'app/hooks/mutations/callflow';

import { useListCallflowsQuery } from 'app/hooks/queries/callflow';
import useWebhook from 'app/pages/v2/pipe/hooks/useWebhook';
import { SettingsPanel } from 'app/pages/v2/settings/shared/SettingsPanel';
import { useHint } from 'app/utilities';
import useDevMode from 'app/utilities/useDevMode';
import useToggleReducer from 'app/utilities/useToggleReducer';
// import { AnimationDialog } from 'app/design';
// import { AnimationDialogProps } from 'app/design/components/AnimationDialog/AnimationDialog';
import { EditPencil } from 'iconoir-react';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

function MessageRouting({ pipe }) {
  // shows the callflows for the user_ids of the pipe
  const router = useHistory();
  const [{ openTour }] = useGlobalTour();
  const [openDrawer, toggleOpenDrawer] = useToggleReducer(false);

  const [isHiddenVisualBuilder, hideHint, refetchHints, setHintValue] = useHint(
    'admin.setup.callflow.visual_builder',
  );

  const {
    data: callflowsPage,
    isLoading: callflowsLoading,
    refetch,
  } = useListCallflowsQuery({
    take: 1,
    filters: {
      raw: [{ variables: { docFilters: [['numbers', 'contains', pipe.key]] } }],
    },
    options: {},
  });
  const callflows = callflowsPage?.callflows ?? [];
  const callflow = callflows?.length ? callflows[0] : null;

  return (
    <SettingsPanel
      id="message_routing"
      leftChild={
        <>
          <Setting
            label={'Message routing'}
            body={'Auto-respond to incoming messages'}
            iconButtonProps={
              callflow
                ? {
                    children: <EditPencil fr={undefined} />,
                    onClick: () => {
                      router.push(
                        `/v2/settings/pipes/${pipe.id}/message_routing/${callflow.id}`,
                      );
                      // if (!isHiddenVisualBuilder) {
                      //   // openTour('visual_builder');
                      // }
                      // toggleOpenDrawer();
                    },
                  }
                : undefined
            }
          />
          {(process.env.NODE_ENV === 'development' ||
            pipe.type === 'external') &&
          callflow ? (
            <AutorespondToggle
              refetch={refetch}
              callflow={callflow}
              pipe={pipe}
            />
          ) : null}
        </>
      }
      rightChild={
        <div data-tour-settings-lines-view-messagerouting>
          <div className="space-y-4">
            {!callflowsLoading && !callflow ? (
              <div className="bg-gray-50 rounded-lg py-5 px-6 mb-4 text-base text-gray-500 mb-3">
                Call routing not yet setup
              </div>
            ) : (
              <div>
                <div
                  className={`relative w-full h-[300px] rounded-md ${
                    callflow ? 'bg-neutral-100' : ''
                  }`}
                >
                  {/* Hide the QuickDisplay when the Drawer is open, otherwise we get weird errors when editing a callflow */}
                  {openDrawer ? null : callflowsLoading ? (
                    <SettingsPanelLoader label={'Loading message routing...'} />
                  ) : callflow ? (
                    <MessageRoutingQuickDisplay
                      pipe={pipe}
                      // id={JSON.stringify(callflow)}
                      // key={mainCallflowKey} // causes a rerender when changed!
                      callflow={callflow.doc}
                      showControls
                    />
                  ) : null}
                  {/* <MessageList callflow={callflow} /> */}
                </div>
                {/*)}*/}
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}

const AutorespondToggle = ({ pipe, refetch, callflow }) => {
  const updateCallflow = useUpdateCallflowPartial();
  const { devMode } = useDevMode();
  const isEnabled = callflow?.doc?.message_enabled ? true : false;

  const toggleEnabled = async () => {
    if (updateCallflow.isLoading) {
      return false;
    }
    // TODO: change to Pipe, instead of Callflow?
    let resp = await updateCallflow.mutateAsync({
      id: callflow.id,
      message_enabled: !isEnabled,
    });
    refetch();
    console.log('RESP:', resp);
  };

  return (
    <div className={`mt-4`}>
      <Switch
        size="md"
        label={'Enable auto-respond'}
        checked={isEnabled}
        onClick={toggleEnabled}
        isLoading={updateCallflow.isLoading}
      />
      {devMode ? <TestAutoReply to={pipe.key} /> : null}
    </div>
  );
};

const TestAutoReply = ({ to }) => {
  const formMethods = useForm({
    defaultValues: {
      number: process.env.REACT_APP_AUTO_REPLY_NUMBER ?? 'test',
      message: 'test',
    },
  });

  const mutation = useWebhook();

  const onSubmit = async ({ number, message }: any) => {
    await mutation.mutateAsync({
      carrier_id: 'sys_bandwidth',
      log_id: '9e9af014-5640-4bf1-9094-ce6b07bdb91a',
      type: 'message-received',
      time: new Date(),
      to: to,
      message: {
        id: uuidv4(),
        to: [to],
        from: number,
        time: new Date(),
        direction: 'in',
        text: message,
        media: [],
        segment_count: 1,
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <div className={'mt-8'}>Test auto-reply (DEBUG)</div>
      <br />
      <HookFormTextField name={'number'} label={'From'} />
      <br />
      <HookFormTextField name={'message'} label={'Message'} />
      <br />
      <Button onClick={formMethods.handleSubmit(onSubmit)}>Send</Button>
    </FormProvider>
  );
};

const MessageList = ({ callflow }) => {
  // merge a default list of options with whatever exists
  // - ids for all!

  const updateCallflow = useUpdateCallflowPartial();

  const defaults = [
    {
      id: 'basic1',
      name: '',
      is_default: true,
      message_strategy: {
        // we separate "message_strategy" and "message_flow"
        type: 'exact',
        // modules begin here! module: {id, type, data, children}
        data: {
          id: uuidv4(),
          type: 'send_message',
          data: {
            text: '',
          },
          children: {},
        },
      },
    },
  ];

  const handleUpdate = async () => {
    let resp = await updateCallflow.mutateAsync({
      id: callflow.id,
      message_strategy: {
        type: 'blank',
        data: {
          modules: [],
        },
        data2: {
          id: uuidv4(),
          type: 'match_generic',
          data: {
            match: ['convo_no_messages'],
          },
          children: {
            rate_limit: null, // could be a different message we send?
            match: {
              id: uuidv4(),
              type: 'send_message',
              data: {
                text: 'testing a MATCh message',
              },
              children: {
                rate_limit: null,
              },
            },
            no_match: {
              id: uuidv4(),
              type: 'send_message',
              data: {
                text: 'testing no_match message',
              },
              children: {
                rate_limit: null,
              },
            },
            _next: {
              id: uuidv4(),
              type: 'send_message',
              data: {
                text: 'This is the NEXT message',
              },
              children: {
                rate_limit: null,
              },
            },
          },
        },
      }, //callflow.message_strategy,
    });
    console.log('RESP:', resp);
  };

  // simple options (for building correct flow)
  // - when to Suppress (not send)
  //   - default rate-limit of not-more-than-3 of same type/module within 3 minutes
  //     - TODO: how to add the tracking to the message-sending?
  //       - check the PipeMessage where "PipeMessage.data.autoreply_module_id" (saved on server, auto-generated uuid)

  // allow customer to save different flows, and swap between them?
  // - let them add/remove responses?
  // - they may want to change all Lines at the same time? To a consistent message? (v2)

  // auto-reply not in use (default)
  // [create custom auto-reply]

  // - react-flow
  // - react-diagrams
  // - react-d3-tree

  return (
    <div className="">
      <div className="">Choose from our default message auto-replies:</div>
      <div className=""></div>
      <div className="">Or build your own auto-reply strategy...</div>
    </div>
  );
};

export default MessageRouting;
